import React, { useState } from 'react';
import moment from 'moment';
import {
  ArrowBack,
  CalendarTodayOutlined,
  CheckCircleOutlined,
  PlayArrowOutlined,
  ScaleOutlined,
  ScheduleOutlined,
  StopOutlined,
} from '@mui/icons-material';
import { Box, Button, Chip, Paper, TextField, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { Colors } from '../../../assets/theme/Colors';
import { MARKER_DONOR, MARKER_RECEIVER } from '../../Map/Components/MarkersSelector';
import RouteChip from './RouteChip';
import CircularCounter from './CircularCounter';
import { frquencyTypes } from './RoutesFrequency';
import {
  FREQUENCY_EVERY_OTHER_WEEK,
  FREQUENCY_MONTHLY,
  FREQUENCY_ONCE,
  FREQUENCY_WEEKLY,
} from '../../../models/donationsNew';
import { createRoute } from '../../../actions/routes';
import routes from '../../../routes';
import snackbarHelper from '../../../helpers/snackbarHelper';
import { monthlyTypes } from './RouteSchedule';

const NewRouteReview = ({
  handleCancelNewRoute,
  handlePrevious,
  stops = [],
  stopDetails = {},
  scheduleDetails = {},
  distanceAndDuration = {},
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const activeRoutes = useSelector((state) => state.entities.routes);
  const [routeName, setRouteName] = useState('');
  const selectedFrequencyType = frquencyTypes.find((frequency) => frequency.value === scheduleDetails.frequency);
  const weeklyDays = scheduleDetails?.weekly_days;
  const weeklyDaysFormatted = (scheduleDetails?.weekly_days || []).map((day) => moment(day, 'dddd').format('ddd'));
  const monthlyDays = scheduleDetails.monthly_days.sort(
    (a, b) => (a.ordinal === -1 ? Infinity : a.ordinal) - (b.ordinal === -1 ? Infinity : b.ordinal)
  );
  const monthlyDaysFormatted = monthlyDays.flatMap(({ ordinal, days }) => {
    const label = monthlyTypes.find((t) => t.value === ordinal)?.label;
    return days.map((day) => `${label} ${moment(day, 'dddd').format('ddd')}`);
  });
  const oneTimeOnlyDay = moment(scheduleDetails.date, 'YYYY-MM-DD').format('dddd');
  const stopDetailsArray = Object.values(stopDetails);
  const pickups = stops.filter((stop) => stop.type === MARKER_DONOR);
  const dropoffs = stops.filter((stop) => stop.type === MARKER_RECEIVER);

  const lbsEstimated = stopDetailsArray.reduce((acc, curr) => {
    if (curr.type === MARKER_DONOR) {
      return acc + Number(curr.weight);
    }

    return acc;
  }, 0);

  const { pickupWindowStart, pickupWindowEnds } = stopDetailsArray.reduce(
    (acc, curr) => {
      const start = moment(curr.pickup_begin, 'HH:mm"ss');
      const end = moment(curr.pickup_end, 'HH:mm:ss');

      if (start.isBefore(moment(acc.pickupWindowStart, 'HH:mm"ss'))) {
        acc.pickupWindowStart = start.format('hh:mm A');
      }
      if (end.isAfter(moment(acc.pickupWindowEnds, 'HH:mm"ss'))) {
        acc.pickupWindowEnds = end.format('hh:mm A');
      }

      return acc;
    },
    {
      pickupWindowStart: moment(stopDetailsArray[0].pickup_begin, 'HH:mm"ss').format('hh:mm A'),
      pickupWindowEnds: moment(stopDetailsArray[0].pickup_end, 'HH:mm:ss').format('hh:mm A'),
    }
  );

  const handleNameChange = (value) => {
    setRouteName(value);
  };

  const getRouteDays = (short = false) => {
    if (selectedFrequencyType.value === FREQUENCY_ONCE) {
      if (short) {
        return [moment(oneTimeOnlyDay, 'dddd').format('ddd')];
      }
      return [oneTimeOnlyDay];
    }

    if (selectedFrequencyType.value === FREQUENCY_MONTHLY) {
      if (short) {
        return monthlyDaysFormatted;
      }
      return monthlyDays;
    }

    if (
      selectedFrequencyType.value === FREQUENCY_EVERY_OTHER_WEEK ||
      selectedFrequencyType.value === FREQUENCY_WEEKLY
    ) {
      if (short) {
        return weeklyDaysFormatted;
      }
      return weeklyDays;
    }

    return [];
  };

  const handleSubmit = () => {
    const activeRoutesArray = Object.values(activeRoutes.byId || []);
    const destinations = stops.map((stop) => {
      const stopDetail = stopDetails[stop.id];

      return stopDetail;
    });

    const route = {
      id: activeRoutesArray.length + 1,
      name: routeName,
      startTime: pickupWindowStart,
      endTime: pickupWindowEnds,
      pickups: pickups.length,
      dropoffs: dropoffs.length,
      distance: distanceAndDuration?.distance || null,
      duration: distanceAndDuration?.duration || null,
      days: getRouteDays(false),
      formattedDays: getRouteDays(true),
      ...scheduleDetails,
      destinations: [...destinations],
    };

    dispatch(createRoute(route));
    handleCancelNewRoute();
    snackbarHelper.success('Route created');
    return history.push(generatePath(routes.routes));
  };

  return (
    <Box sx={{ minWidth: '500px', ml: 0.5, display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={25} sx={{ p: 2, borderRadius: 2 }}>
        <Box>
          <Button
            startIcon={<ArrowBack />}
            size="small"
            variant="text"
            disableRipple
            onClick={handlePrevious}
            sx={{
              ml: -1,
              color: 'GrayText',
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Back to schedule
          </Button>
          <Typography variant="body1" sx={{ fontWeight: '700' }}>
            Review & Save
          </Typography>
          <Typography variant="caption" sx={{ color: 'GrayText' }}>
            {stops.length} stops {lbsEstimated ? `- ${lbsEstimated} lbs estimated` : ''}{' '}
            {pickupWindowStart && pickupWindowEnds ? `- ${pickupWindowStart} - ${pickupWindowEnds}` : ''}
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ mt: 1, px: 2, height: 'calc(100vh - 250px)', overflow: 'scroll' }}>
        {stops.map((stop, index) => {
          const details = stopDetails[stop.id];

          return (
            <Paper key={stop.id} elevation={25} sx={{ p: 1, borderRadius: 2, my: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularCounter
                    showPrimaryColor={stop.type === MARKER_DONOR}
                    counter={index + 1}
                    size={28}
                    fontSize={12}
                  />
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {stop?.title || stop?.name || stop?.address}
                    </Typography>
                  </Box>
                </Box>
                <RouteChip isDonor={stop.type === MARKER_DONOR} showIcon />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {(details?.categories || []).map((category) => (
                  <Chip
                    key={category}
                    size="small"
                    label={category}
                    sx={{
                      bgcolor: Colors.lightGray,
                      borderRadius: 2,
                      color: 'GrayText',
                      fontWeight: 700,
                      flexShrink: 0,
                      userSelect: 'none',
                    }}
                  />
                ))}
                {!details?.categories?.length && (
                  <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
                    No categories selected
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <ScaleOutlined sx={{ color: 'GrayText', fontSize: '16px' }} />
                  <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'GrayText' }}>
                    {details?.weight || 'Weight TBD'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 2 }}>
                  <ScheduleOutlined sx={{ color: 'GrayText', fontSize: '16px' }} />
                  <Typography variant="caption" sx={{ color: 'GrayText' }}>
                    {moment(details?.pickup_begin, 'HH:mm:ss').format('HH:mm A')} -{' '}
                    {moment(details?.pickup_end, 'HH:mm:ss').format('HH:mm A')}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}

        <Box sx={{ my: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'GrayText' }}>
            SCHEDULE
          </Typography>
          <Paper elevation={25} sx={{ p: 1, borderRadius: 2, my: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ color: Colors.appBar.main, mr: 1 }}>{selectedFrequencyType.icon}</Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {selectedFrequencyType.label}
              </Typography>
            </Box>
            {selectedFrequencyType.value === FREQUENCY_ONCE && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                  <CalendarTodayOutlined sx={{ color: 'GrayText', fontSize: '16px', mr: 1 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'GrayText' }}>
                    Date
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {moment(scheduleDetails.date, 'YYYY-MM-DD').format('MM/DD/YYYY')}
                  </Typography>
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                <CalendarTodayOutlined sx={{ color: 'GrayText', fontSize: '16px', mr: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'GrayText' }}>
                  Days
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {(getRouteDays(true) || []).length > 0 && `${(getRouteDays(true) || []).join(' · ')}`}
                </Typography>
              </Box>
            </Box>
            {selectedFrequencyType.value !== FREQUENCY_ONCE && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                    <PlayArrowOutlined sx={{ color: 'GrayText', fontSize: '16px', mr: 1 }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'GrayText' }}>
                      Starts
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {moment(scheduleDetails.start_date, 'YYYY-MM-DD').format('MM/DD/YYYY')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                    <StopOutlined sx={{ color: 'GrayText', fontSize: '16px', mr: 1 }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'GrayText' }}>
                      End
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {scheduleDetails.end_date
                        ? moment(scheduleDetails.end_date, 'YYYY-MM-DD').format('MM/DD/YYYY')
                        : ''}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>
      <Paper elevation={25} sx={{ p: 2, borderRadius: 2, mt: 'auto' }}>
        <Box>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Name This Route
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Downtown Morning Loop"
              onChange={({ target }) => handleNameChange(target.value)}
            />
          </Box>
          <Box sx={{ my: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {selectedFrequencyType.value === FREQUENCY_ONCE ? 'Rescuer' : 'Adopter'}
            </Typography>
            <TextField fullWidth size="small" placeholder="Search by name or email" />
          </Box>
          <Button
            variant="contained"
            fullWidth
            startIcon={<CheckCircleOutlined />}
            disabled={!routeName}
            sx={{
              mt: 1,
              textTransform: 'none',
              bgcolor: Colors.appBar.main,
              color: Colors.white,
              fontWeight: 700,
              '&:hover': {
                bgcolor: Colors.appBar.light,
              },
            }}
            onClick={handleSubmit}
          >
            Save route
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default NewRouteReview;
